/**
 * CreativePreview — renders an actual on-brand creative from the
 * structured visual_spec the engine returns. Different layouts per
 * direction; no shared template skeleton.
 *
 * Surfaces supported:
 *   flyer (1080x1350)  ·  ig_post (1080x1080)  ·  ig_story (1080x1920)
 *   linkedin (1200x627)  ·  ad_version (1080x1080)  ·  email_header (1200x400)
 *
 * Layouts (per direction.visual_spec.layout):
 *   centered_editorial  ·  asymmetric_left  ·  asymmetric_right
 *   grid_split          ·  stacked_minimal  ·  magazine_cover
 *
 * Each layout reads palette/typography/composition from the spec and
 * places copy with intentional whitespace + typographic hierarchy.
 */
import React from 'react';

export const RENDER_W = 540;  // half of 1080 — keeps DOM nodes small but visuals crisp

const FONT_MAP = {
  display_serif: "'Instrument Serif', Georgia, serif",
  serif:         "'Instrument Serif', Georgia, serif",
  modern_sans:   "'Inter', 'Helvetica Neue', sans-serif",
  bold_sans:     "'Inter', 'Helvetica Neue', sans-serif",
  mono:          "'JetBrains Mono', monospace",
};

const SURFACE_SIZES = {
  flyer:        { w: 1080, h: 1350 },
  ig_post:      { w: 1080, h: 1080 },
  ig_story:     { w: 1080, h: 1920 },
  linkedin:     { w: 1200, h: 627  },
  ad_version:   { w: 1080, h: 1080 },
  email_header: { w: 1200, h: 400  },
};

function applyTexture(textureName) {
  if (textureName === 'grain' || textureName === 'noise') {
    return {
      backgroundImage:
        'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'160\' height=\'160\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'2\' stitchTiles=\'stitch\'/><feColorMatrix values=\'0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>")',
    };
  }
  if (textureName === 'paper') {
    return { backgroundImage: 'radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px)', backgroundSize: '6px 6px' };
  }
  return {};
}

/* ──── Layout primitives ──── */

const CornerBrand = ({ spec, accent }) => (
  <div style={{
    position: 'absolute', top: 26, left: 30,
    display: 'flex', alignItems: 'center', gap: 8,
    fontFamily: FONT_MAP.mono, fontSize: 11, letterSpacing: '0.22em',
    color: spec.text_color, opacity: 0.6, textTransform: 'uppercase',
  }}>
    <span style={{
      width: 12, height: 12, borderRadius: 3, background: accent || spec.accent_color,
      display: 'inline-block',
    }} />
    {spec.mood || 'campaign'}
  </div>
);

const FooterMeta = ({ spec, leftText, rightText }) => (
  <div style={{
    position: 'absolute', bottom: 24, left: 30, right: 30,
    display: 'flex', justifyContent: 'space-between',
    fontFamily: FONT_MAP.mono, fontSize: 10, letterSpacing: '0.2em',
    color: spec.text_color, opacity: 0.45, textTransform: 'uppercase',
  }}>
    <span>{leftText}</span>
    <span>{rightText}</span>
  </div>
);

const CTAButton = ({ text, palette, size = 'md' }) => {
  const pad = size === 'lg' ? '14px 26px' : size === 'sm' ? '7px 14px' : '11px 20px';
  const fontSize = size === 'lg' ? 16 : size === 'sm' ? 11 : 13;
  return (
    <span style={{
      display: 'inline-block', padding: pad,
      background: palette.accent_color || '#c8f060', color: palette.background || '#0a0a0a',
      fontFamily: FONT_MAP.modern_sans, fontWeight: 700, fontSize,
      borderRadius: 999, letterSpacing: '0.02em',
    }}>{text}</span>
  );
};

/* ──── Layout: centered_editorial ──── */

const CenteredEditorialLayout = ({ spec, headline, subhead, body, cta, surfaceSize }) => {
  const isSquare = Math.abs(surfaceSize.w - surfaceSize.h) < 50;
  const headlineFont = FONT_MAP[spec.typography?.headline_family] || FONT_MAP.display_serif;
  return (
    <div style={{
      width: '100%', height: '100%', padding: '14% 10%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', position: 'relative',
      background: spec.background, color: spec.text_color,
      ...applyTexture(spec.texture),
    }}>
      <CornerBrand spec={spec} />
      <div style={{ width: 36, height: 1, background: spec.accent_color, marginBottom: 24, opacity: 0.7 }} />
      <h1 style={{
        fontFamily: headlineFont, fontSize: isSquare ? '4.2vw' : '5vw', lineHeight: 1.04,
        fontWeight: 500, letterSpacing: '-0.015em', margin: 0,
        maxWidth: '90%',
      }}>{headline}</h1>
      {subhead && (
        <p style={{
          fontFamily: FONT_MAP.modern_sans, fontSize: '1.15vw', lineHeight: 1.5,
          color: spec.text_color, opacity: 0.7, margin: '24px 0 0', maxWidth: '70%',
        }}>{subhead}</p>
      )}
      {body && (
        <p style={{ fontFamily: FONT_MAP.modern_sans, fontSize: '0.95vw', margin: '20px 0 0', opacity: 0.55, maxWidth: '64%' }}>{body}</p>
      )}
      {cta && <div style={{ marginTop: 36 }}><CTAButton text={cta} palette={spec} size="md" /></div>}
      <FooterMeta spec={spec} leftText={spec.layout} rightText="Editorial" />
    </div>
  );
};

/* ──── Layout: asymmetric_left ──── */

const AsymmetricLeftLayout = ({ spec, headline, subhead, body, cta }) => {
  const headlineFont = FONT_MAP[spec.typography?.headline_family] || FONT_MAP.bold_sans;
  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      background: spec.background, color: spec.text_color, display: 'flex', flexDirection: 'column',
      padding: '8% 8% 8% 8%',
      ...applyTexture(spec.texture),
    }}>
      {/* Accent diagonal band */}
      <div style={{
        position: 'absolute', top: '40%', right: '-10%', width: '50%', height: '6px',
        background: spec.accent_color, transform: 'rotate(-12deg)',
      }} />
      <CornerBrand spec={spec} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', textAlign: 'left', marginTop: 50 }}>
        <h1 style={{
          fontFamily: headlineFont, fontSize: '6vw', lineHeight: 0.96,
          fontWeight: 800, letterSpacing: '-0.025em', margin: 0,
          textTransform: 'uppercase',
        }}>{headline}</h1>
        {subhead && (
          <p style={{
            fontFamily: FONT_MAP.modern_sans, fontSize: '1.4vw', lineHeight: 1.45,
            color: spec.text_color, opacity: 0.8, margin: '24px 0 0', maxWidth: '70%', fontWeight: 500,
          }}>{subhead}</p>
        )}
        {body && (
          <p style={{ fontFamily: FONT_MAP.modern_sans, fontSize: '1vw', margin: '20px 0 0', opacity: 0.55, maxWidth: '60%' }}>{body}</p>
        )}
        {cta && <div style={{ marginTop: 32 }}><CTAButton text={cta} palette={spec} size="lg" /></div>}
      </div>
      <FooterMeta spec={spec} leftText="Modern · Bold" rightText={spec.mood} />
    </div>
  );
};

/* ──── Layout: stacked_minimal (conversion) ──── */

const StackedMinimalLayout = ({ spec, headline, subhead, body, cta }) => {
  const headlineFont = FONT_MAP[spec.typography?.headline_family] || FONT_MAP.bold_sans;
  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      background: spec.background, color: spec.text_color,
      padding: '9% 8%', display: 'flex', flexDirection: 'column',
    }}>
      <CornerBrand spec={spec} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', marginTop: 56 }}>
        {/* Tiny eyebrow */}
        <p style={{
          fontFamily: FONT_MAP.mono, fontSize: '0.9vw', letterSpacing: '0.22em',
          color: spec.accent_color, textTransform: 'uppercase', margin: 0, fontWeight: 700,
        }}>{spec.mood || 'new'}</p>
        <h1 style={{
          fontFamily: headlineFont, fontSize: '5.6vw', lineHeight: 1.0,
          fontWeight: 800, letterSpacing: '-0.022em', margin: '14px 0 24px', maxWidth: '92%',
        }}>{headline}</h1>
        {subhead && (
          <p style={{ fontFamily: FONT_MAP.modern_sans, fontSize: '1.35vw', lineHeight: 1.45, opacity: 0.8, margin: 0, maxWidth: '85%' }}>
            {subhead}
          </p>
        )}
        {/* Value props line */}
        {body && (
          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {String(body).split(/[·•|]/).slice(0, 4).map((b, i) => b.trim() && (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: spec.accent_color }} />
                <span style={{ fontFamily: FONT_MAP.modern_sans, fontSize: '1vw', opacity: 0.85 }}>{b.trim()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {cta && (
        <div style={{ marginTop: 'auto', paddingTop: 24 }}>
          <CTAButton text={cta} palette={spec} size="lg" />
        </div>
      )}
      <FooterMeta spec={spec} leftText="Conversion creative" rightText={spec.mood} />
    </div>
  );
};

/* ──── Layout: asymmetric_right ──── */

const AsymmetricRightLayout = ({ spec, headline, subhead, body, cta }) => {
  const headlineFont = FONT_MAP[spec.typography?.headline_family] || FONT_MAP.bold_sans;
  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      background: spec.background, color: spec.text_color, padding: '8%',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
      ...applyTexture(spec.texture),
    }}>
      <div style={{
        position: 'absolute', top: '0', left: '0', width: '45%', height: '70%',
        background: spec.accent_color, opacity: 0.95,
      }} />
      <CornerBrand spec={spec} />
      <div style={{ position: 'relative', maxWidth: '55%', textAlign: 'right' }}>
        <h1 style={{
          fontFamily: headlineFont, fontSize: '5.4vw', lineHeight: 1.02,
          fontWeight: 700, letterSpacing: '-0.015em', margin: 0,
        }}>{headline}</h1>
        {subhead && (
          <p style={{ fontFamily: FONT_MAP.modern_sans, fontSize: '1.2vw', lineHeight: 1.5, opacity: 0.75, margin: '20px 0 0' }}>{subhead}</p>
        )}
        {cta && <div style={{ marginTop: 28 }}><CTAButton text={cta} palette={spec} size="md" /></div>}
      </div>
      <FooterMeta spec={spec} leftText="Asymmetric" rightText={spec.mood} />
    </div>
  );
};

/* ──── Layout: grid_split ──── */

const GridSplitLayout = ({ spec, headline, subhead, body, cta }) => {
  const headlineFont = FONT_MAP[spec.typography?.headline_family] || FONT_MAP.modern_sans;
  const palette = spec.palette || [];
  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr',
      background: spec.background, color: spec.text_color,
    }}>
      <div style={{ padding: 36, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: spec.accent_color, color: spec.background }}>
        <p style={{ fontFamily: FONT_MAP.mono, fontSize: '0.9vw', letterSpacing: '0.2em', textTransform: 'uppercase', margin: 0, opacity: 0.85 }}>{spec.mood}</p>
      </div>
      <div style={{ padding: 36, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: palette[2] || '#1a1a1a' }} />
      <div style={{ padding: 36, display: 'flex', flexDirection: 'column', justifyContent: 'center', background: spec.background }}>
        <h1 style={{ fontFamily: headlineFont, fontSize: '3.6vw', lineHeight: 1.05, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>{headline}</h1>
        {subhead && <p style={{ fontFamily: FONT_MAP.modern_sans, fontSize: '1.1vw', opacity: 0.7, margin: '14px 0 0' }}>{subhead}</p>}
      </div>
      <div style={{ padding: 36, display: 'flex', flexDirection: 'column', justifyContent: 'center', background: palette[3] || palette[1] || '#222', color: spec.background }}>
        {body && <p style={{ fontFamily: FONT_MAP.modern_sans, fontSize: '1vw', lineHeight: 1.5, opacity: 0.9, margin: 0 }}>{body}</p>}
        {cta && <div style={{ marginTop: 18 }}><CTAButton text={cta} palette={{ accent_color: spec.background, background: spec.accent_color }} size="md" /></div>}
      </div>
      <CornerBrand spec={spec} />
    </div>
  );
};

/* ──── Layout: magazine_cover ──── */

const MagazineCoverLayout = ({ spec, headline, subhead, body, cta }) => {
  const headlineFont = FONT_MAP[spec.typography?.headline_family] || FONT_MAP.display_serif;
  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      background: spec.background, color: spec.text_color,
      padding: '7% 7%', display: 'flex', flexDirection: 'column',
      ...applyTexture(spec.texture),
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        borderBottom: `1px solid ${spec.text_color}33`, paddingBottom: 14,
      }}>
        <p style={{ fontFamily: FONT_MAP.display_serif, fontSize: '2vw', margin: 0, fontWeight: 500, letterSpacing: '-0.02em' }}>
          {spec.mood || 'Issue 01'}
        </p>
        <p style={{ fontFamily: FONT_MAP.mono, fontSize: '0.85vw', letterSpacing: '0.18em', textTransform: 'uppercase', margin: 0, opacity: 0.55 }}>Vol. 01</p>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <h1 style={{
          fontFamily: headlineFont, fontSize: '6.5vw', lineHeight: 0.92,
          fontWeight: 500, letterSpacing: '-0.03em', margin: 0,
        }}>{headline}</h1>
        {subhead && (
          <p style={{ fontFamily: FONT_MAP.display_serif, fontSize: '1.8vw', lineHeight: 1.3, fontStyle: 'italic', opacity: 0.75, margin: '24px 0 0', maxWidth: '75%' }}>
            {subhead}
          </p>
        )}
        {cta && <div style={{ marginTop: 28 }}><CTAButton text={cta} palette={spec} size="lg" /></div>}
      </div>
    </div>
  );
};

/* ──── Layout dispatcher ──── */

const LAYOUTS = {
  centered_editorial: CenteredEditorialLayout,
  asymmetric_left:    AsymmetricLeftLayout,
  asymmetric_right:   AsymmetricRightLayout,
  stacked_minimal:    StackedMinimalLayout,
  grid_split:         GridSplitLayout,
  magazine_cover:     MagazineCoverLayout,
};

/* ──── Public component ──── */

export function CreativePreview({ surface = 'flyer', direction, copy, surfaceCopy, slide, placeholderText }) {
  const spec = direction?.visual_spec || {};
  const surfaceSize = SURFACE_SIZES[surface] || SURFACE_SIZES.flyer;
  const Layout = LAYOUTS[spec.layout] || CenteredEditorialLayout;
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const illustrationUrl = slide?.illustration_url ? `${apiBase}${slide.illustration_url}` : null;

  // Pick text fields based on surface + slide
  let headline, subhead, body, cta;
  if (slide) {
    headline = slide.headline;
    subhead = slide.body || '';
    body = '';
    cta = slide.role === 'cta' ? (copy?.cta_primary || 'See more') : '';
  } else if (surfaceCopy) {
    headline = surfaceCopy.headline || copy?.headline || 'Headline';
    subhead = surfaceCopy.subheadline || surfaceCopy.subhead || copy?.subheadline || '';
    body = surfaceCopy.body || surfaceCopy.caption || copy?.supporting_copy || '';
    cta = surfaceCopy.cta || copy?.cta_primary || '';
  } else if (copy) {
    headline = copy.headline || 'Headline';
    subhead = copy.subheadline || '';
    body = copy.supporting_copy || '';
    cta = copy.cta_primary || '';
  } else {
    // Direction preview — use placeholderText
    headline = placeholderText || direction?.name || 'Your campaign';
    subhead = direction?.best_for || direction?.feels_like?.join(' · ') || '';
    body = '';
    cta = '';
  }

  const aspectRatio = surfaceSize.w / surfaceSize.h;

  return (
    <div style={{ width: '100%', aspectRatio: `${surfaceSize.w} / ${surfaceSize.h}`, position: 'relative', overflow: 'hidden', containerType: 'inline-size' }}>
      <Layout spec={spec} headline={headline} subhead={subhead} body={body} cta={cta} surfaceSize={surfaceSize} />
      {illustrationUrl && (
        <div style={{
          position: 'absolute',
          top: '52%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '46%', aspectRatio: '1 / 1', borderRadius: '50%',
          overflow: 'hidden', pointerEvents: 'none',
          boxShadow: '0 20px 60px -20px rgba(0,0,0,0.5)',
          opacity: 0.92, mixBlendMode: 'normal',
        }}>
          <img src={illustrationUrl} alt="" crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
    </div>
  );
}
